import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  DollarSign, 
  Plus, 
  Edit, 
  Trash2, 
  ArrowLeft,
  Calendar,
  Target,
  TrendingUp,
  Eye,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from 'sonner';
import SEO from '../components/SEO';

const CampaignManager = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    goal_amount: '',
    description: '',
    story: '',
    image_url: '',
    impact_metric: '',
    start_date: '',
    end_date: '',
    status: 'active'
  });

  useEffect(() => {
    verifyAuth();
    fetchCampaigns();
  }, []);

  const verifyAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/verify`,
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
    } catch (err) {
      localStorage.removeItem('admin_token');
      navigate('/admin/login');
    }
  };

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/campaigns`,
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      setCampaigns(response.data);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = () => {
    setEditingCampaign(null);
    setFormData({
      name: '',
      goal_amount: '',
      description: '',
      story: '',
      image_url: '',
      impact_metric: '₹2000 = 2 elders trained',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      status: 'active'
    });
    setShowModal(true);
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      goal_amount: campaign.goal_amount,
      description: campaign.description,
      story: campaign.story || '',
      image_url: campaign.image_url || '',
      impact_metric: campaign.impact_metric,
      start_date: campaign.start_date,
      end_date: campaign.end_date || '',
      status: campaign.status
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    try {
      if (editingCampaign) {
        // Update existing campaign
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/campaigns/${editingCampaign.id}`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` }}
        );
        toast.success('Campaign updated successfully!');
      } else {
        // Create new campaign
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/campaigns`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` }}
        );
        toast.success('Campaign created successfully!');
      }
      
      setShowModal(false);
      fetchCampaigns();
    } catch (err) {
      console.error('Error saving campaign:', err);
      toast.error('Failed to save campaign');
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/campaigns/${campaignId}`,
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      toast.success('Campaign deleted successfully');
      fetchCampaigns();
    } catch (err) {
      console.error('Error deleting campaign:', err);
      toast.error('Failed to delete campaign');
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'active': return <CheckCircle size={18} color="#4caf50" />;
      case 'completed': return <XCircle size={18} color="#9e9e9e" />;
      case 'inactive': return <Clock size={18} color="#ff9800" />;
      default: return null;
    }
  };

  const activeCampaignsCount = campaigns.filter(c => c.status === 'active').length;

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-content" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
          <p>Loading campaigns...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <SEO title="Campaign Manager - Admin" description="Manage fundraising campaigns" />

      {/* Header */}
      <div className="admin-header">
        <div className="container">
          <div className="admin-header-content">
            <div>
              <Button 
                onClick={() => navigate('/admin')} 
                variant="outline"
                style={{ marginBottom: '1rem', background: 'white' }}
              >
                <ArrowLeft size={20} />
                Back to Dashboard
              </Button>
              <h1 className="admin-title">Campaign Manager</h1>
              <p className="admin-subtitle">
                {activeCampaignsCount} of 3 active campaigns • {campaigns.length} total campaigns
              </p>
            </div>
            <div className="admin-header-actions">
              <Button 
                onClick={handleCreateCampaign}
                className="btn-primary-large"
                disabled={activeCampaignsCount >= 3}
              >
                <Plus size={20} />
                Create Campaign
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="admin-content">
        <div className="container">
          {activeCampaignsCount >= 3 && (
            <div className="campaign-warning">
              <AlertCircle size={20} />
              You've reached the maximum of 3 active campaigns. Deactivate a campaign to create a new one.
            </div>
          )}

          {/* Campaigns Grid */}
          <div className="campaigns-grid">
            {campaigns.map(campaign => (
              <div key={campaign.id} className="campaign-card">
                <div className="campaign-card-header">
                  <div className="campaign-status-badge">
                    {getStatusIcon(campaign.status)}
                    <span>{campaign.status}</span>
                  </div>
                  <div className="campaign-actions">
                    <button onClick={() => handleEditCampaign(campaign)} className="action-btn">
                      <Edit size={18} />
                    </button>
                    <button onClick={() => handleDeleteCampaign(campaign.id)} className="action-btn delete-btn">
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                <h3 className="campaign-card-title">{campaign.name}</h3>
                <p className="campaign-card-description">{campaign.description}</p>

                <div className="campaign-card-stats">
                  <div className="campaign-stat">
                    <Target size={18} />
                    <div>
                      <span className="stat-label">Goal</span>
                      <span className="stat-value">₹{campaign.goal_amount.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="campaign-stat">
                    <Calendar size={18} />
                    <div>
                      <span className="stat-label">Start Date</span>
                      <span className="stat-value">{campaign.start_date}</span>
                    </div>
                  </div>
                </div>

                <div className="campaign-card-footer">
                  <span className="impact-text">{campaign.impact_metric}</span>
                  <Button 
                    variant="outline"
                    onClick={() => window.open(`/campaign/${campaign.slug}`, '_blank')}
                  >
                    <Eye size={16} />
                    View
                  </Button>
                </div>
              </div>
            ))}

            {campaigns.length === 0 && (
              <div className="empty-campaigns">
                <DollarSign size={64} color="#e0e0e0" />
                <h3>No campaigns yet</h3>
                <p>Create your first fundraising campaign to get started</p>
                <Button onClick={handleCreateCampaign} className="btn-primary-large">
                  <Plus size={20} />
                  Create Campaign
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="campaign-modal">
          <DialogHeader>
            <DialogTitle>
              {editingCampaign ? 'Edit Campaign' : 'Create New Campaign'}
            </DialogTitle>
            <DialogDescription>
              {editingCampaign ? 'Update campaign details' : 'Fill in the campaign information'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="campaign-form">
            <div className="form-group">
              <label>Campaign Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                placeholder="e.g., Digital Literacy Drive 2024"
              />
            </div>

            <div className="form-group">
              <label>Goal Amount (₹) *</label>
              <Input
                type="number"
                value={formData.goal_amount}
                onChange={(e) => setFormData({...formData, goal_amount: e.target.value})}
                required
                placeholder="100000"
              />
            </div>

            <div className="form-group">
              <label>Short Description *</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                required
                placeholder="Brief description for the campaign card"
                rows={2}
              />
            </div>

            <div className="form-group">
              <label>Campaign Story</label>
              <Textarea
                value={formData.story}
                onChange={(e) => setFormData({...formData, story: e.target.value})}
                placeholder="Full story/details about the campaign"
                rows={4}
              />
            </div>

            <div className="form-group">
              <label>Impact Metric *</label>
              <Input
                value={formData.impact_metric}
                onChange={(e) => setFormData({...formData, impact_metric: e.target.value})}
                required
                placeholder="e.g., ₹2000 = 2 elders trained"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Start Date *</label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Status *</label>
              <select 
                className="form-select"
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <div className="form-group">
              <label>Image URL</label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                placeholder="https://example.com/image.jpg"
              />
            </div>

            <Button type="submit" className="submit-button">
              {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignManager;
